import React, { useState, useEffect } from "react";
import { X, User, Package, GripVertical, ChevronUp, ChevronDown, Clock, MapPin, Sparkles } from "lucide-react";
import deliveryAgentsApi from "../../api/delivery-agents";
import CustomDropdown from "../../components/common/CustomDropdown";

interface BulkAssignDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (agent: any, sequenceDetails: any) => void;
  selectedOrders: any[];
}

interface OrderItem {
  _id: string;
  customerName: string;
  mealName: string;
  deliveryTime: string;
  address: string;
  postcode: string;
  batchNumber: number;
  batchDate: string;
  originalOrder: any;
}

const BulkAssignDialog: React.FC<BulkAssignDialogProps> = ({
  isOpen,
  onClose,
  onAssign,
  selectedOrders,
}) => {
  const [deliveryAgents, setDeliveryAgents] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [defaultBatch, setDefaultBatch] = useState<number>(1);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchDeliveryAgents = async () => {
      try {
        const { deliveryAgents } = await deliveryAgentsApi.getDeliveryAgents({ isActive: true });
        setDeliveryAgents(deliveryAgents);
      } catch (error) {
        console.error("Error fetching delivery agents:", error);
      }
    };

    if (isOpen) {
      fetchDeliveryAgents();
      setSelectedAgent(null);
      setDefaultBatch(1);

      if (selectedOrders && selectedOrders.length > 0) {
        // Pre-sort orders: Time Slot -> Pincode -> Address
        const sorted = [...selectedOrders].sort((a, b) => {
          const slotA = (a.deliveryTime || "").toLowerCase();
          const slotB = (b.deliveryTime || "").toLowerCase();
          if (slotA !== slotB) return slotA.localeCompare(slotB);

          const pinA = (a.deliveryDetails?.postcode || "").toLowerCase();
          const pinB = (b.deliveryDetails?.postcode || "").toLowerCase();
          if (pinA !== pinB) return pinA.localeCompare(pinB);

          const addrA = (a.deliveryDetails?.address || "").toLowerCase();
          const addrB = (b.deliveryDetails?.address || "").toLowerCase();
          return addrA.localeCompare(addrB);
        });

        const formattedItems: OrderItem[] = sorted.map((o) => {
          let dateStr = o.batchDate;
          if (!dateStr && o.deliveryDate) {
            dateStr = new Date(o.deliveryDate).toISOString().split("T")[0];
          }
          if (!dateStr) {
            dateStr = new Date().toISOString().split("T")[0];
          }

          let cName = o.userId?.name || "Customer";
          if (o.deliveryDetails?.name && o.userId?.name && o.deliveryDetails.name.trim().toLowerCase() !== o.userId.name.trim().toLowerCase()) {
            cName = `${o.deliveryDetails.name} (${o.userId.name})`;
          } else if (o.deliveryDetails?.name) {
            cName = o.deliveryDetails.name;
          }

          return {
            _id: o._id,
            customerName: cName,
            mealName: o.mealId?.name || "Meal",
            deliveryTime: o.deliveryTime || "Morning",
            address: o.deliveryDetails?.address || "Address unavailable",
            postcode: o.deliveryDetails?.postcode || "",
            batchNumber: o.batchNumber || 1,
            batchDate: dateStr,
            originalOrder: o,
          };
        });

        setOrderItems(formattedItems);
      } else {
        setOrderItems([]);
      }
    }
  }, [isOpen, selectedOrders]);

  // Apply default batch to all items
  const handleApplyDefaultBatch = (batchNum: number) => {
    setDefaultBatch(batchNum);
    setOrderItems((prev) =>
      prev.map((item) => ({
        ...item,
        batchNumber: batchNum,
      }))
    );
  };

  // Change batch for single item
  const handleItemBatchChange = (index: number, batchNum: number) => {
    setOrderItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], batchNumber: batchNum };
      return updated;
    });
  };

  // Move item up or down
  const moveItem = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= orderItems.length) return;

    setOrderItems((prev) => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[targetIndex];
      updated[targetIndex] = temp;
      return updated;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setOrderItems((prev) => {
      const updated = [...prev];
      const draggedItem = updated[draggedIndex];
      updated.splice(draggedIndex, 1);
      updated.splice(index, 0, draggedItem);
      return updated;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAssign = () => {
    if (selectedAgent && orderItems.length > 0) {
      // Pass the items with sequence numbers matching their position
      const sequencePayload = orderItems.map((item, idx) => ({
        _id: item._id,
        batchNumber: item.batchNumber,
        batchDate: item.batchDate,
        sequenceInBatch: idx + 1,
      }));
      onAssign(selectedAgent, sequencePayload);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Bulk Assign Orders</h2>
              <p className="text-xs text-gray-500">
                {selectedOrders.length} orders selected • Pre-sorted by Time & Pincode
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content area */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-gray-50/50">
          {/* Rider Selection */}
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-2">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
              Select Delivery Agent
            </label>
            <CustomDropdown
              options={deliveryAgents.map((agent) => ({
                value: agent._id,
                label: `${agent.name} (${agent.phoneNumber}) - ${agent.bikeNumber || "No Bike"}`,
                ...agent,
              }))}
              value={selectedAgent?._id || ""}
              onChange={(value) => {
                const agent = deliveryAgents.find((a) => a._id === value);
                setSelectedAgent(agent);
              }}
              placeholder="Choose a rider for these orders..."
            />
          </div>

          {/* Quick Batch Set Bar */}
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-2 text-xs font-semibold text-gray-700">
              <Sparkles className="h-4 w-4 text-orange-500" />
              <span>Set Batch for All Selected Orders:</span>
            </div>
            <div className="flex items-center space-x-1.5">
              {[1, 2, 3, 4].map((bNum) => (
                <button
                  key={bNum}
                  type="button"
                  onClick={() => handleApplyDefaultBatch(bNum)}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${
                    defaultBatch === bNum
                      ? "bg-orange-500 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Batch {bNum}
                </button>
              ))}
            </div>
          </div>

          {/* Reorderable Orders List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Route Order & Batch Settings ({orderItems.length} Stops)
              </span>
              <span className="text-[11px] text-gray-400">
                Drag handles or use arrows to adjust sequence
              </span>
            </div>

            <div className="space-y-2">
              {orderItems.map((item, index) => {
                const isDragging = draggedIndex === index;

                return (
                  <div
                    key={item._id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`bg-white p-3.5 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                      isDragging
                        ? "border-orange-500 shadow-md opacity-60 bg-orange-50/20"
                        : "border-gray-200 hover:border-gray-300 shadow-sm"
                    }`}
                  >
                    {/* Left controls: Sequence badge + Drag handle + Up/Down arrows */}
                    <div className="flex items-center space-x-2">
                      <div className="cursor-grab text-gray-300 hover:text-gray-600 p-1">
                        <GripVertical className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => moveItem(index, "up")}
                          className="text-gray-400 hover:text-orange-500 disabled:opacity-20 disabled:hover:text-gray-400"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={index === orderItems.length - 1}
                          onClick={() => moveItem(index, "down")}
                          className="text-gray-400 hover:text-orange-500 disabled:opacity-20 disabled:hover:text-gray-400"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 font-bold text-xs flex items-center justify-center">
                        #{index + 1}
                      </div>
                    </div>

                    {/* Middle: Customer & Address Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm text-gray-900 truncate">
                          {item.customerName}
                        </span>
                        <span className="text-xs text-gray-400 font-normal">
                          • {item.mealName}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {item.address}
                      </p>
                      <div className="flex items-center space-x-2 mt-1.5">
                        {item.postcode && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                            <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                            {item.postcode}
                          </span>
                        )}
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                          <Clock className="h-3 w-3 mr-1 text-blue-500" />
                          {item.deliveryTime}
                        </span>
                      </div>
                    </div>

                    {/* Right: Per-Item Batch Dropdown */}
                    <div className="flex-shrink-0">
                      <select
                        value={item.batchNumber}
                        onChange={(e) => handleItemBatchChange(index, parseInt(e.target.value))}
                        className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold bg-white text-gray-700 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      >
                        <option value={1}>Batch 1</option>
                        <option value={2}>Batch 2</option>
                        <option value={3}>Batch 3</option>
                        <option value={4}>Batch 4</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {selectedAgent ? (
              <span>Assigning to <strong className="text-gray-900">{selectedAgent.name}</strong></span>
            ) : (
              <span>Please select a rider above</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedAgent || orderItems.length === 0}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              Assign {orderItems.length} Orders
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignDialog;